import jsPDF from 'jspdf';

interface AgreementData {
    name: string;
    content: string;
}

interface GenerateAgreementPDFParams {
    agreements: AgreementData[];
    visitorName: string;
    agreedText: string;
}

/**
 * Generate a PDF document containing all agreed agreements
 * Format matches the Android implementation exactly
 */
export const generateAgreementPDF = ({
    agreements,
    visitorName,
    agreedText,
}: GenerateAgreementPDFParams): Blob => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4', // 595 x 842 pt
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 50; // Increased from 30pt for better spacing
    const contentWidth = pageWidth - 2 * margin;
    const lineHeight = 1.5; // Increased from 1.15 for better readability
    const bodyFontSize = 11;
    const titleFontSize = 16; // Larger title font

    agreements.forEach((agreement, agreementIndex) => {
        const { name: title, content } = agreement;

        // Add new page for each agreement (except first)
        if (agreementIndex > 0) {
            doc.addPage();
        }

        let yPosition = margin;

        // Title on first page - centered, larger font
        doc.setFontSize(titleFontSize);
        doc.setFont('helvetica', 'bold');
        const titleLines = doc.splitTextToSize(title, contentWidth);
        const titleHeight = titleLines.length * titleFontSize * lineHeight;
        doc.text(titleLines, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += titleHeight + 30; // Increased spacing from 20 to 30

        // Content - left aligned
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(bodyFontSize);
        const contentLines = doc.splitTextToSize(content, contentWidth);

        contentLines.forEach((line: string) => {
            // Check if we need a new page (leave space for signature section)
            if (yPosition + bodyFontSize * lineHeight > pageHeight - margin - 180) {
                doc.addPage();
                yPosition = margin;
            }

            doc.text(line, margin, yPosition);
            yPosition += bodyFontSize * lineHeight;
        });

        // Calculate if signature section fits on current page
        const signatureSectionHeight = bodyFontSize * lineHeight * 5; // Approximate height needed
        if (yPosition + signatureSectionHeight > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
        }

        // Add spacing before signature section - increased for better separation
        yPosition += 50; // Increased from 40

        // Agreement text - right aligned
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(bodyFontSize);
        const agreementTextLines = doc.splitTextToSize(agreedText, contentWidth - 100);
        agreementTextLines.forEach((line: string) => {
            doc.text(line, pageWidth - margin, yPosition, { align: 'right' });
            yPosition += bodyFontSize * lineHeight;
        });

        // Add space between agreement text and name
        yPosition += 25; // Slightly increased from 20

        // Visitor name - right aligned
        doc.text(visitorName, pageWidth - margin, yPosition, { align: 'right' });
        yPosition += bodyFontSize * lineHeight;

        // Date - right aligned (YYYY-MM-DD format to match the image)
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        doc.text(formattedDate, pageWidth - margin, yPosition, { align: 'right' });
    });

    // Convert to Blob
    const pdfBlob = doc.output('blob');
    return pdfBlob;
};
